import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Copy, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

const KIND_LABEL = { offerings: "Offering", events: "Event / class time slot", programs: "Program", packages: "Package", centers: "Center", enhancers: "Enhancer", gauge_profiles: "Gauge / showcase profile" };
const STATUS = {
  needs_review: { text: "Possible duplicate — needs review", cls: "bg-[#B5794E]/10 text-[#B5794E]" },
  reviewed: { text: "Reviewed", cls: "bg-[#5C8A72]/10 text-[#5C8A72]" },
  not_duplicate: { text: "Not a duplicate", cls: "bg-[#144F75]/10 text-[#144F75]" },
};

function GroupCard({ g, i }) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (status) => api.post("/admin/duplicates/review", { group_id: g.group_id, status }),
    onSuccess: () => { toast.success("Review saved"); qc.invalidateQueries({ queryKey: ["admin-duplicates"] }); },
    onError: (e) => toast.error(formatApiError(e?.response?.data?.detail) || "We couldn't save that review. Please try again."),
  });
  const s = STATUS[g.status] || STATUS.needs_review;
  return (
    <div className="eyw-card p-5" data-testid={`dup-group-${i}`}>
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-[#8FA89B]">{KIND_LABEL[g.kind] || g.kind} · {g.count} records</p>
          <p className="font-serif text-lg text-[#1A242B]">{g.label}</p>
        </div>
        <span className={`text-xs font-semibold rounded-full px-3 py-1 ${s.cls}`} data-testid={`dup-status-${i}`}>{s.text}</span>
      </div>
      <ul className="mt-3 divide-y divide-[#E5E0D8] text-sm">
        {g.records.map((r) => (
          <li key={r.id} className="py-2 flex flex-wrap justify-between gap-2" data-testid={`dup-record-${r.id}`}>
            <span className="text-[#1A242B]">{r.label} <span className="text-[#8FA89B]">· {r.owner || "—"}</span></span>
            <span className="text-xs text-[#4A5D6B]">{r.published === false ? "Hidden" : "Live"} · ID {r.id.slice(-6)}{r.created_at ? ` · added ${fmtDateTime(r.created_at)}` : ""}</span>
          </li>
        ))}
      </ul>
      <div className="mt-4 flex flex-wrap gap-2">
        <button className="eyw-btn-ghost text-sm" disabled={m.isPending} onClick={() => m.mutate("reviewed")} data-testid={`dup-mark-reviewed-${i}`}>Mark reviewed</button>
        <button className="eyw-btn-ghost text-sm" disabled={m.isPending} onClick={() => m.mutate("not_duplicate")} data-testid={`dup-mark-not-${i}`}>Not a duplicate</button>
        {g.status !== "needs_review" && <button className="eyw-btn-ghost text-sm" disabled={m.isPending} onClick={() => m.mutate("needs_review")} data-testid={`dup-reopen-${i}`}>Reopen</button>}
      </div>
    </div>
  );
}

export default function DuplicatesPanel() {
  const [kind, setKind] = useState("all");
  const { data, isLoading } = useQuery({ queryKey: ["admin-duplicates"], queryFn: () => api.get("/admin/duplicates").then((r) => r.data) });
  const groups = (data?.groups || []).filter((g) => kind === "all" || g.kind === kind);
  return (
    <div data-testid="duplicates-panel">
      <div className="flex items-center gap-3"><Copy className="text-[#144F75]" /><h2 className="font-serif text-2xl text-[#1A242B]">Duplicate review</h2></div>
      <p className="text-sm text-[#4A5D6B] mt-1 max-w-2xl">Records that share the same title, owner and type (or the same time slot at the same location). Nothing here is changed or removed automatically. Review each group, then merge or deactivate the extra record from its own editor.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {["all", ...Object.keys(KIND_LABEL)].map((k) => (
          <button key={k} onClick={() => setKind(k)} data-testid={`dup-filter-${k}`} className={`text-xs rounded-full px-3 py-1.5 border ${kind === k ? "bg-[#144F75] text-white border-[#144F75]" : "border-[#144F75]/20 text-[#144F75]"}`}>{k === "all" ? "All" : KIND_LABEL[k]}</button>
        ))}
      </div>
      {isLoading ? <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-[#144F75]" /></div>
        : groups.length === 0 ? (
          <div className="eyw-card p-8 mt-6 text-center" data-testid="dup-empty">
            <ShieldCheck className="mx-auto text-[#5C8A72]" size={32} />
            <p className="font-serif text-lg text-[#1A242B] mt-2">No duplicates found</p>
            <p className="text-sm text-[#4A5D6B]">Every listing is unique. New duplicates are blocked when someone tries to create them.</p>
          </div>
        ) : (
          <>
            <p className="text-sm mt-5 text-[#1A242B]" data-testid="dup-summary"><CheckCircle2 size={14} className="inline text-[#B5794E]" /> {data.needs_review} group{data.needs_review === 1 ? "" : "s"} need review</p>
            <div className="mt-3 grid gap-4">{groups.map((g, i) => <GroupCard key={g.group_id} g={g} i={i} />)}</div>
          </>
        )}
    </div>
  );
}
